import type {
  BlockEnvelope,
  BlockKey,
  CreateLookupRequest,
  CreateLookupResponse,
  LookupStreamEvent,
  PlanDto,
  ResolveResponse,
  SourceDto,
} from '@foco/contracts';
import { API_BASE, apiFetch } from './client';

/**
 * The API surface the web client is allowed to call (spec 4.3, 13.7).
 *
 * Kept as a thin, typed façade over `apiFetch` so that a screen never builds a
 * URL by hand and so that the "some of these are not implemented yet" reality
 * is visible in one file: every function here can reject with an `ApiError`, and
 * every screen must render a loading/error/empty state because of it.
 */

/** `POST /api/v1/search/resolve` — identifier vs name disambiguation (spec 5). */
export function resolveSearch(
  query: string,
  options: { signal?: AbortSignal } = {},
): Promise<ResolveResponse> {
  return apiFetch<ResolveResponse>('/search/resolve', {
    method: 'POST',
    json: { query },
    signal: options.signal,
  });
}

/**
 * `POST /api/v1/lookup` — start a check. Returns immediately with the block list
 * the server intends to fill; the data itself arrives over SSE.
 */
export function createLookup(
  body: CreateLookupRequest,
  options: { signal?: AbortSignal } = {},
): Promise<CreateLookupResponse> {
  return apiFetch<CreateLookupResponse>('/lookup', {
    method: 'POST',
    json: body,
    signal: options.signal,
    // The server renders the block headings and the risk text itself, so the
    // language travels explicitly. `X-Locale` would do; the query parameter is
    // used as well because it survives a cached or replayed request and is
    // visible in a log when a card comes back in the wrong language.
    query: { locale: documentLocale() },
    // A cold lookup can touch a dozen slow registries.
    timeoutMs: 30_000,
  });
}

/** The active interface language, as the server expects it. */
function documentLocale(): string {
  if (typeof document === 'undefined') return 'es';
  return document.documentElement.lang || 'es';
}

/**
 * `GET /api/v1/lookup/:id/blocks/:key` — the polling fallback used when
 * `EventSource` cannot connect (proxy strips `text/event-stream`, corporate
 * firewall, Safari tab suspension). One block per call so a single slow registry
 * never blocks the others.
 */
export function fetchBlock(
  lookupId: string,
  key: BlockKey,
  options: { signal?: AbortSignal } = {},
): Promise<BlockEnvelope> {
  return apiFetch<BlockEnvelope>(
    `/lookup/${encodeURIComponent(lookupId)}/blocks/${encodeURIComponent(key)}`,
    { signal: options.signal },
  );
}

/**
 * URL for the SSE stream. `EventSource` cannot set headers or a body, so the
 * lookup id travels in the path and the session cookie does the authentication
 * (which is why `apiFetch` uses `credentials: 'include'` everywhere else too).
 */
export function lookupStreamUrl(lookupId: string): string {
  return `${API_BASE}/lookup/${encodeURIComponent(lookupId)}/stream`;
}

/** `GET /api/v1/plans` — tariff cards on the landing page and /pricing. */
export function fetchPlans(options: { signal?: AbortSignal } = {}): Promise<PlanDto[]> {
  return apiFetch<PlanDto[]>('/plans', { signal: options.signal });
}

/** `GET /api/v1/sources` — the live source matrix (spec 3). */
export function fetchSources(options: { signal?: AbortSignal } = {}): Promise<SourceDto[]> {
  return apiFetch<SourceDto[]>('/sources', { signal: options.signal });
}

/* ------------------------------------------------------------------ *
 * SSE parsing
 * ------------------------------------------------------------------ */

/**
 * Decode one SSE frame into a `LookupStreamEvent`, or `null` when the frame is
 * a comment/keep-alive (`: ping`) or a heart-beat event the client ignores.
 *
 * `EventSource` already parses frames for us, but the polling fallback and the
 * tests use this too, and having one strict decoder means a malformed frame from
 * a proxy can never crash the card.
 */
export function parseStreamEvent(raw: string): LookupStreamEvent | null {
  if (!raw || raw.startsWith(':')) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const type = (parsed as { type?: unknown }).type;
  if (type === 'block' || type === 'risk' || type === 'done' || type === 'error') {
    return parsed as LookupStreamEvent;
  }
  // Unknown event kinds are ignored on purpose: the server may add new ones and
  // an old client must keep working (the union is discriminated, so this is the
  // only place the check has to happen).
  return null;
}
